import { PropType, defineComponent, ref } from 'vue'

import { CsTag, MsHeadline } from '@sunrise/components'
import { MsBookmarkFilled, MsDoor, MsFloorPlan, MsSurface } from '@sunrise/icons'
import { componentUtils, useGlobalStore } from '@sunrise/shared'
import { track, trackLink } from '@sunrise/tracking'

import { Bookmark } from '../../ts/mapper/bookmarkMapper.interface'
import { Status } from '../../ts/service/realEstateService.enum'
import { useBookmarkStore } from '../../ts/store/bookmarkStore/bookmarkStoreModule'

import MsFallbackSvg from '../../static/illus/fallback.svg?component'
import MsImmoweltBadge from '../../static/illus/immoweltBadge.svg?component'
import { buildTealiumRemoveBookmarkData, getTrackingDataForItem } from './MsBookmarkItemTracking'

export default defineComponent({
    name: 'MsBookmarkItem',
    components: {
        CsTag,
        MsBookmarkFilled,
        MsDoor,
        MsFloorPlan,
        MsHeadline,
        MsSurface,
        MsFallbackSvg,
        MsImmoweltBadge,
    },
    props: {
        bookmarkItem: {
            type: Object as PropType<Bookmark>,
            required: true,
        },
        isOwnInventory: {
            type: Boolean,
        },
        componentId: {
            type: String,
            required: true,
        },
        position: {
            type: Number,
            required: true,
        },
    },
    emits: ['onBookmarkRemoved'],
    setup(props, { emit }) {
        const requestContext = useGlobalStore().requestContext
        const currentUrl = requestContext?.url
        const fallBackExpiredExpose = componentUtils.createAssetLink('/images/fallback_abgl_anzeigen.jpg')
        const isImmoweltInventoryOffline = ref<boolean>(false)

        function clickItem(e: MouseEvent): void {
            const trackingData = getTrackingDataForItem(
                props.bookmarkItem,
                props.componentId,
                currentUrl?.toString() || '',
                props.position,
                props.isOwnInventory,
            )
            const target = e.currentTarget as HTMLAnchorElement
            trackLink(e, target, trackingData)
        }

        function showExposeImage(bookmarkItem: Bookmark): boolean {
            return !!bookmarkItem.smallImageUrl || isOwnInventoryOffline(bookmarkItem)
        }

        function isOwnInventoryOffline(bookmarkItem: Bookmark): boolean {
            return props.isOwnInventory && bookmarkItem.status !== Status.ONLINE
        }

        function removeBookmark(): void {
            useBookmarkStore().removeBookmarkById(props.bookmarkItem.guid)
            const tealiumData = buildTealiumRemoveBookmarkData(props.bookmarkItem, props.isOwnInventory)
            track(tealiumData)

            emit('onBookmarkRemoved', props.bookmarkItem.guid)
        }

        function showLandArea(): boolean {
            return props.bookmarkItem.livingArea == null || props.bookmarkItem.rooms == null
        }

        function getOfferType(): string {
            return props.bookmarkItem.commercialExpose ? 'gewerbliches' : 'privates'
        }

        function onImageError(event: Event) {
            const target = event.target as HTMLImageElement
            target.src = fallBackExpiredExpose
            isImmoweltInventoryOffline.value = true
        }

        function onImageLoad(event: Event) {
            if (!props.isOwnInventory) {
                const target = event.target as HTMLImageElement
                if (!target.src.includes(fallBackExpiredExpose)) {
                    isImmoweltInventoryOffline.value = false
                }
            }
        }

        return {
            fallBackExpiredExpose,
            isImmoweltInventoryOffline,
            clickItem,
            showExposeImage,
            isOwnInventoryOffline,
            removeBookmark,
            showLandArea,
            getOfferType,
            onImageError,
            onImageLoad,
        }
    },
})
